import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

export const CHAT_SYNC_DOWN_GRACE_MS = 10_000;

interface ChatOfflineIndicatorProps {
    /** Chat initialisation failed and has not succeeded since. */
    initFailed: boolean;
    /** The failure needs user action, so no automatic retry is pending. */
    permanentFailure?: boolean;
    /** True once the Matrix client finished initialising. */
    isChatInitialized: boolean;
    syncState: string;
    onRetry: () => void;
    syncDownGraceMs?: number;
}

const isSyncDown = (syncState: string) => syncState === 'ERROR' || syncState === 'STOPPED';

export function ChatOfflineIndicator({
    initFailed,
    permanentFailure = false,
    isChatInitialized,
    syncState,
    onRetry,
    syncDownGraceMs = CHAT_SYNC_DOWN_GRACE_MS,
}: ChatOfflineIndicatorProps) {
    const intl = useIntl();
    const syncDown = isChatInitialized && isSyncDown(syncState);
    const [syncDownLong, setSyncDownLong] = useState(false);

    // Sync flaps through ERROR on every brief network blip; only show the
    // indicator once it has stayed down past the grace period.
    useEffect(() => {
        if (!syncDown) {
            setSyncDownLong(false);
            return;
        }
        const timer = setTimeout(() => setSyncDownLong(true), syncDownGraceMs);
        return () => clearTimeout(timer);
    }, [syncDown, syncDownGraceMs]);

    if (!initFailed && !syncDownLong) return null;

    const label = permanentFailure && initFailed
        ? intl.formatMessage({id: 'chat.offline.retry', defaultMessage: 'Chat offline — click to retry'})
        : intl.formatMessage({id: 'chat.offline.reconnecting', defaultMessage: 'Chat offline — reconnecting…'});

    return (
        <button
            type="button"
            data-testid="chat-offline-indicator"
            role="status"
            title={intl.formatMessage({id: 'chat.offline.tooltip', defaultMessage: 'Click to retry now'})}
            onClick={onRetry}
            className="fixed bottom-3 left-3 z-50 rounded-full border border-warning-line bg-warning-tint px-3 py-1 text-xs text-warning-ink shadow hover:bg-warning-tint"
        >
            {label}
        </button>
    );
}
